import { RouteObject, createBrowserRouter, redirect } from "react-router-dom";
import { useAuthStore } from "@/stores/auth/auth-store";
import { queryClient } from "@/api/client";
import { currentUserQueryOptions } from "@/api/queries/useCurrentUser";
import { workspaceArtistsQueryOptions } from "@/api/queries/useArtists";
import { artistQueryOptions } from "@/api/queries/useArtist";
import { toursQueryOptions } from "@/api/queries/useTours";
import { GenericError } from "../Errors/GenericError";
import { personalDetailsQueryOptions } from "@/api/queries/usePersonalDetails";
import { travellingDetailsQueryOptions } from "@/api/queries/useTravellingDetails";
import { emergencyContactsQueryOptions } from "@/api/queries/useEmergencyContacts";
import { tourQueryOptions } from "@/api/queries/useTour";
import { tourDaysQueryOptions } from "@/api/queries/useTourDays";
import { eventTypesQueryOptions } from "@/api/queries/useEventTypes";
import { DashboardErrorBoundary } from "../Dashboard/DashboardErrorBoundary";
import { riderQueryOptions } from "@/api/queries/useRider";
import { riderPersonnelQueryOptions } from "@/api/queries/useRiderPersonnel";
import { riderExternalPersonnelQueryOptions } from "@/api/queries/useRiderExternalPersonnel";
import { chatRoomsQueryOptions } from "@/api/queries/useChatRooms";
import { reportQuestionsColumnsQueryOptions } from "@/api/queries/useReportQuestionsColumns";
import { userTermsAcceptanceQueryOptions } from "@/api/queries/useUserTermsAcceptance";
import { userPrivacyPolicyAcceptanceQueryOptions } from "@/api/queries/useUserPrivacyPolicyAcceptance";
import { teamMembersQueryOptions } from "../../api/queries/useTeamMembers";
import { teamQueryOptions } from "../../api/queries/useTeam";
import { teamMemberWagesQueryOptions } from "../../api/queries/useTeamMemberWages";
import { teamsQueryOptions } from "../../api/queries/useTeams";
import { teamEstimatesQueryOptions } from "../../api/queries/useTeamEstimates";
import { estimateQueryOptions } from "../../api/queries/useEstimate";
import { eventQueryOptions } from "@/api/queries/useEvent";
import { teamInvitationsQueryOptions } from "@/api/queries/useTeamInvitations.ts";
import { taskQueryOptions } from "@/api/queries/useTask.ts";
import { roomsQueryOptions } from "@/api/queries/useRooms";
import { eventPersonnelListQueryOptions } from "@/api/queries/useEventPersonnelList";
import { advanceVenueSubmissionQueryOptions } from "@/api/queries/useAdvanceVenueSubmission.ts";
import { TourRoles } from "@articulate/shared";
import * as Sentry from "@sentry/react";

const ACCEPT_TOUR_INVITATION = "accept-invitation";

const routes = [
  {
    path: "*",
    element: <GenericError is404Page />,
  },
  {
    path: "",
    loader: () => {
      return redirect("/login");
    },
  },
  {
    path: "",
    errorElement: <GenericError />,
    loader: async ({ request }) => {
      const accessToken = useAuthStore.getState().authToken;
      if (!accessToken) {
        return {};
      }

      const isVenueAccount = (
        await queryClient.fetchQuery(currentUserQueryOptions)
      ).isVenue;
      if (isVenueAccount) {
        return redirect("/venue-form-submissions/messages");
      }

      if (request.url.includes("confirm-reset-password")) {
        return {};
      }

      return redirect("/dashboard");
    },
    lazy: () => import("../Auth/Unauthorized.layout"),
    children: [
      {
        path: "login",
        lazy: () => import("../Auth/Login.page"),
      },
      {
        path: "register",
        lazy: () => import("../Auth/Register.page"),
      },
      {
        path: "register-confirm",
        lazy: () => import("../Auth/RegisterConfirm.page"),
      },
      {
        path: "register-complete",
        lazy: () => import("../Auth/RegisterComplete.page"),
      },
      {
        path: "reset-password",
        lazy: () => import("../Auth/ResetPassword.page"),
      },
      {
        path: "reset-password/sent",
        lazy: () => import("../Auth/ResetPasswordConfirm.page"),
      },
      {
        path: "confirm-reset-password",
        lazy: () => import("../Auth/ConfirmResetPassword.page"),
      },
    ],
  },
  {
    path: "onboarding",
    lazy: () => import("../Onboarding/Onboarding.layout"),
    loader: async () => {
      const accessToken = useAuthStore.getState().authToken;
      if (!accessToken) {
        return redirect("/login");
      }

      await queryClient.ensureQueryData(currentUserQueryOptions);

      return {};
    },
    children: [
      {
        path: "",
        lazy: () => import("../Onboarding/Onboarding.page"),
      },
      {
        path: "0",
        lazy: () => import("../Onboarding/0Onboarding.page"),
      },
      {
        path: "1",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/1Onboarding.page"),
      },
      {
        path: "2",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/2Onboarding.page"),
      },
      {
        path: "3",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/3Onboarding.page"),
      },
      {
        path: "4",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/4Onboarding.page"),
      },
      {
        path: "5",
        loader: async () => {
          await queryClient.prefetchQuery(travellingDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/5Onboarding.page"),
      },
      {
        path: "6",
        loader: async () => {
          await queryClient.prefetchQuery(travellingDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/6Onboarding.page"),
      },
      {
        path: "7",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/7Onboarding.page"),
      },
      {
        path: "8",
        loader: async () => {
          await queryClient.prefetchQuery(emergencyContactsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/8Onboarding.page"),
      },
      {
        path: "9",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/9Onboarding.page"),
      },
      {
        path: "10",
        loader: async () => {
          await queryClient.prefetchQuery(personalDetailsQueryOptions);
          return {};
        },
        lazy: () => import("../Onboarding/10Onboarding.page"),
      },
    ],
  },
  {
    path: "/terms-and-conditions",
    lazy: () => import("../TermsAndConditions/TermsAndConditions.layout"),
    loader: async () => {
      const accessToken = useAuthStore.getState().authToken;
      if (!accessToken) {
        return redirect("/login");
      }

      await queryClient.ensureQueryData(currentUserQueryOptions);

      return {};
    },
  },
  {
    path: "venue-form-submissions",
    lazy: () => import("../Dashboard/Dashboard.layout"),
    children: [
      {
        path: "edit/:venueSubmissionId",
        lazy: () => import("../Artist/Advance/AdvanceVenueForm.page"),
        loader: async ({ params }) => {
          if (!params.venueSubmissionId) {
            return {};
          }
          await queryClient.prefetchQuery(
            advanceVenueSubmissionQueryOptions(params.venueSubmissionId)
          );
          return {};
        },
      },
      {
        path: "messages",
        lazy: () => import("../Messages/Messages.page"),
      },
    ],
  },
  {
    path: "dashboard",
    lazy: () => import("../Dashboard/Dashboard.layout"),
    errorElement: <GenericError />,
    loader: async () => {
      const accessToken = useAuthStore.getState().authToken;
      if (!accessToken) {
        return redirect("/login");
      }

      const isVenueAccount = (
        await queryClient.fetchQuery(currentUserQueryOptions)
      ).isVenue;
      if (isVenueAccount) {
        return redirect("/venue-form-submissions/messages");
      }

      const [termsAcceptance, privacyPolicyAcceptance] = await Promise.all([
        queryClient.ensureQueryData(userTermsAcceptanceQueryOptions),
        queryClient.ensureQueryData(userPrivacyPolicyAcceptanceQueryOptions),
      ]);
      const areTermsAccepted = termsAcceptance?.accepted;
      const arePrivacyPolicyAccepted = privacyPolicyAcceptance?.accepted;

      if (!areTermsAccepted || !arePrivacyPolicyAccepted) {
        return redirect("/terms-and-conditions");
      }

      const [currentUser] = await Promise.all([
        queryClient.ensureQueryData(currentUserQueryOptions),
        queryClient.ensureQueryData(workspaceArtistsQueryOptions()),
      ]);
      const showOnboarding =
        !currentUser.status.onboardingCompleted &&
        !currentUser.status.onboardingSkipped;

      if (showOnboarding) {
        return redirect("/onboarding");
      }

      return {};
    },
    children: [
      {
        path: "",
        element: <DashboardErrorBoundary fallback={<GenericError />} />,
        children: [
          {
            path: "*",
            element: <GenericError is404Page />,
          },
          {
            path: "",
            loader: async () => {
              await queryClient.prefetchQuery(toursQueryOptions());
              return {};
            },
            lazy: () => import("../Dashboard/Dashboard.page"),
          },
          {
            path: "profile",
            lazy: () => import("../Profile/Profile.page"),
          },
          {
            path: "invitations",
            lazy: () => import("../Dashboard/Invitations/Invitations.page"),
          },
          {
            path: "resources",
            lazy: () => import("../Dashboard/Resources/Resources.page"),
          },
          {
            path: "community",
            lazy: () => import("../Dashboard/Community/Community.page"),
          },
          {
            path: "resources/questionnaire",
            lazy: () => import("../Dashboard/Resources/Questionnaire.page.tsx"),
          },
          {
            path: "library",
            lazy: () => import("../Dashboard/FileLibrary/FileLibrary.page"),
          },
          {
            path: "calendar",
            lazy: () => import("../Dashboard/Calendar/Calendar.page"),
          },
          {
            path: "teams",
            lazy: () => import("../Dashboard/Teams/Teams.page"), // Main TeamsPage
          },
          {
            path: "teams/:teamId",
            lazy: () => import("../Dashboard/Teams/Teams.page"), // Main TeamsPage
            loader: async ({ params }) => {
              if (!params.teamId) {
                return {};
              }
              await queryClient.prefetchQuery(teamsQueryOptions(params.teamId));

              return {};
            },
            children: [
              {
                path: "reports",
                lazy: () =>
                  import("../Dashboard/Teams/Tabs/ReportsTab/ReportsTab"),
              },
              {
                path: "availability",
                lazy: () =>
                  import(
                    "../Dashboard/Teams/Tabs/AvailabilityTab/AvailabilityTab"
                  ),
                loader: async () => {
                  await queryClient.prefetchQuery(eventTypesQueryOptions);

                  return {};
                },
              },
              {
                path: "estimating",
                lazy: () =>
                  import("../Dashboard/Teams/Tabs/EstimatingTab/EstimatingTab"),
                loader: async ({ params }) => {
                  if (!params.teamId) {
                    return {};
                  }
                  await queryClient.prefetchQuery(
                    teamMembersQueryOptions(params.teamId)
                  );
                  await queryClient.prefetchQuery(
                    teamEstimatesQueryOptions(params.teamId)
                  );

                  return {};
                },
              },
              {
                path: "team",
                lazy: () => import("../Dashboard/Teams/Tabs/TeamTab/MyTeamTab"),
              },
            ],
          },
          {
            path: "teams/:teamId/edit",
            lazy: () => import("../Dashboard/Teams/Tabs/TeamTab/TeamForm.page"),
            loader: async ({ params }) => {
              if (!params.teamId) {
                return {};
              }
              await queryClient.prefetchQuery(
                teamMembersQueryOptions(params.teamId)
              );

              await queryClient.prefetchQuery(teamQueryOptions(params.teamId));

              return {};
            },
          },
          {
            path: "teams/add",
            lazy: () => import("../Dashboard/Teams/Tabs/TeamTab/TeamForm.page"),
          },
          {
            path: "teams/:teamId/members/:memberId/wages",
            lazy: () =>
              import("../Dashboard/Teams/Tabs/TeamTab/TeamMemberWages.page"),
          },
          {
            path: "teams/:teamId/members/:memberId/wages/:wageId/edit",
            lazy: () =>
              import("../Dashboard/Teams/Tabs/TeamTab/TeamMemberWageForm.page"),
            loader: async ({ params }) => {
              await queryClient.prefetchQuery(toursQueryOptions());
              if (!params.teamId || !params.memberId) {
                return {};
              }
              await queryClient.prefetchQuery(
                teamMemberWagesQueryOptions(params.teamId, params.memberId)
              );
              return {};
            },
          },
          {
            path: "teams/:teamId/members/:memberId/wages/add",
            lazy: () =>
              import("../Dashboard/Teams/Tabs/TeamTab/TeamMemberWageForm.page"),
            loader: async () => {
              await queryClient.prefetchQuery(toursQueryOptions());

              return {};
            },
          },
          {
            path: "teams/:teamId/estimate/:estimateId",
            lazy: () =>
              import("../Dashboard/Teams/Tabs/EstimatingTab/Estimate.page"),
            loader: async ({ params }) => {
              await queryClient.prefetchQuery(toursQueryOptions());
              if (!params.teamId || !params.estimateId) {
                return {};
              }
              await queryClient.prefetchQuery(
                estimateQueryOptions(params.teamId, params.estimateId)
              );
              return {};
            },
          },
          {
            path: "profile/change-password",
            lazy: () => import("../Profile/ChangePassword.page"),
          },
          {
            path: "profile/personal-details",
            lazy: () => import("../Profile/Forms/PersonalDetailsForm.page"),
          },
          {
            path: "profile/travelling-details",
            lazy: () => import("../Profile/Forms/TravellingDetailsForm.page"),
          },
          {
            path: "profile/emergency-contacts",
            lazy: () => import("../Profile/Forms/EmergencyContactForm.page"),
          },
          {
            path: "messages",
            loader: async () => {
              await queryClient.prefetchQuery(chatRoomsQueryOptions);

              return {};
            },
            lazy: () => import("../Messages/Messages.page"),
          },
          {
            path: "messages/:chatId/edit",
            lazy: () => import("../Messages/CreateChatRoomForm.page"),
          },
          {
            path: "messages/add",
            lazy: () => import("../Messages/CreateChatRoomForm.page"),
          },
          {
            path: "reminders",
            lazy: () => import("../Dashboard/Reminders/RemindersList.page.tsx"),
          },
          {
            path: "tasks",
            lazy: () => import("../Dashboard/Tasks/TasksList.page.tsx"),
          },
          {
            path: "tasks/archived",
            lazy: () => import("../Dashboard/Tasks/ArchivedTasksList.page.tsx"),
          },
          {
            path: "tasks/create",
            lazy: () => import("../Dashboard/Tasks/CreateTask.page.tsx"),
          },
          {
            path: "tasks/:taskId/edit",
            loader: async ({ params }) => {
              if (!params.taskId) return {};

              await queryClient.refetchQueries(taskQueryOptions(params.taskId));

              return {};
            },
            lazy: () => import("../Dashboard/Tasks/EditTask.page.tsx"),
          },
          {
            path: "artists/:artistId",
            loader: async ({ params }) => {
              if (!params.artistId) {
                return {};
              }

              await Promise.all([
                queryClient.prefetchQuery(artistQueryOptions(params.artistId)),
                queryClient.prefetchQuery(
                  toursQueryOptions({
                    filter: {
                      artistId: params.artistId,
                    },
                  })
                ),
              ]);

              return {};
            },
            lazy: () => import("../Artist/Artist.page"),
          },
          {
            path: "artists/create",
            lazy: () => import("../Artist/CreateArtist.page"),
          },
          {
            path: "artists/:artistId/edit",
            loader: async ({ params }) => {
              if (!params.artistId) return {};

              await queryClient.prefetchQuery(
                artistQueryOptions(params.artistId)
              );

              return {};
            },
            lazy: () => import("../Artist/EditArtist.page"),
          },
          {
            path: "tours/create",
            lazy: () => import("../Tours/CreateTour.page"),
          },
          {
            path: "tours/create/:artistId",
            lazy: () => import("../Tours/Forms/CreateTourForm.page"),
          },
          {
            path: "tours/create/:artistId/:tourId",
            loader: async ({ params }) => {
              if (!params.tourId) {
                return {};
              }
              return redirect("assign-personnel");
            },
          },
          {
            path: "tours/create/:artistId/:tourId/assign-venues",
            loader: async ({ params }) => {
              if (!params.tourId) {
                return {};
              }
              await queryClient.ensureQueryData(
                tourDaysQueryOptions(params.tourId)
              );
              await queryClient.prefetchQuery(tourQueryOptions(params.tourId));
              return {};
            },
            lazy: () => import("../Tours/AssignVenuesTourStep.page"),
          },
          {
            path: "tours/create/:artistId/:tourId/assign-personnel",
            lazy: () => import("../Tours/Forms/AssignPersonnelTourForm.page"),
          },
          {
            path: "tours/update/:artistId/:tourId",
            lazy: () => import("../Tours/Forms/UpdateTourForm.page"),
            loader: async ({ params }) => {
              if (!params.tourId) {
                return {};
              }
              await Promise.all([
                queryClient.prefetchQuery(tourQueryOptions(params.tourId)),
                queryClient.prefetchQuery(tourDaysQueryOptions(params.tourId)),
              ]);

              return {};
            },
          },
          {
            path: "tours/update/:artistId/:tourId/assign-venues",
            loader: async ({ params }) => {
              if (!params.tourId) {
                return {};
              }
              await queryClient.ensureQueryData(
                tourDaysQueryOptions(params.tourId)
              );
              await queryClient.prefetchQuery(tourQueryOptions(params.tourId));
              return {};
            },
            lazy: () => import("../Tours/AssignVenuesTourStep.page"),
          },
          {
            path: "tours/update/:artistId/:tourId/assign-personnel",
            lazy: () => import("../Tours/Forms/AssignPersonnelTourForm.page"),
          },
          {
            path: "tours/:tourId",
            lazy: () => import("../Tours/Tour.layout"),
            loader: async ({ params, request }) => {
              const tourId = params.tourId;
              if (!tourId) return {};

              const isAcceptInvitationPage = request.url.includes(
                ACCEPT_TOUR_INVITATION
              );
              if (isAcceptInvitationPage) return {};

              await queryClient.prefetchQuery(tourQueryOptions(tourId));
              await queryClient.prefetchQuery(tourDaysQueryOptions(tourId));
              await queryClient.prefetchQuery(eventTypesQueryOptions);
              return {};
            },
            children: [
              {
                path: "",
                lazy: () => import("../Tours/Tour.page"),
                children: [
                  {
                    path: "",
                    lazy: () => import("../Tours/TourContent.page"),
                  },
                  {
                    path: "event-templates",
                    lazy: () => import("../Tours/Events/EventTemplates.page"),
                  },
                ],
              },
              {
                path: "personnel",
                lazy: () => import("../Tours/Personnel/TourPersonnel.page"),
              },
              {
                path: "personnel/:personnelId",
                lazy: () =>
                  import("../Tours/Personnel/BioPersonnelDetails.page.tsx"),
              },
              {
                path: "daysheets/:tourDay",
                lazy: () => import("../Tours/Daysheets/Daysheets.page"),
              },
              {
                path: "accommodation/:eventId",
                lazy: () => import("../Tours/Accommodation/Accommodation.page"),
                loader: async ({ params }) => {
                  const eventId = params.eventId;
                  if (!eventId) return {};

                  await queryClient.prefetchQuery(eventQueryOptions(eventId));
                  await queryClient.prefetchQuery(
                    eventPersonnelListQueryOptions(eventId)
                  );
                  await queryClient.prefetchQuery(roomsQueryOptions(eventId));
                  return {};
                },
              },
              {
                path: "accommodation/:eventId/edit",
                lazy: () =>
                  import("../Tours/Accommodation/AccommodationEdit.page"),
                loader: async ({ params }) => {
                  const eventId = params.eventId;
                  if (!eventId) return {};

                  await queryClient.prefetchQuery(eventQueryOptions(eventId));
                  return {};
                },
              },
              {
                path: "advance-tools",
                lazy: () => import("../Tours/Riders/AdvanceTools.page"),
              },
              {
                path: "advance-tools/riders/create",
                lazy: () => import("../Tours/Riders/RiderForm.page"),
              },
              {
                path: "advance-tools/riders/:riderId",
                lazy: () => import("../Tours/Riders/Rider.layout"),
                loader: async ({ params }) => {
                  if (!params.riderId) return {};

                  await queryClient.prefetchQuery(
                    riderQueryOptions(params.riderId)
                  );
                  return {};
                },
                children: [
                  {
                    index: true,
                    loader: async ({ params }) => {
                      if (!params.riderId) return {};

                      await queryClient.prefetchQuery(
                        riderPersonnelQueryOptions(params.riderId)
                      );
                      await queryClient.prefetchQuery(
                        riderExternalPersonnelQueryOptions(params.riderId)
                      );
                      return redirect("personnel");
                    },
                  },
                  {
                    path: "personnel",
                    lazy: () => import("../Tours/Riders/Rider01Personnel.page"),
                  },
                  {
                    path: "labor",
                    lazy: () => import("../Tours/Riders/Rider02Labor.page"),
                  },
                  {
                    path: "venue",
                    lazy: () => import("../Tours/Riders/Rider03Venue.page"),
                  },
                  {
                    path: "transportation",
                    lazy: () =>
                      import("../Tours/Riders/Rider04Transportation.page"),
                  },
                  {
                    path: "technical",
                    lazy: () => import("../Tours/Riders/Rider05Technical.page"),
                  },
                  {
                    path: "hospitality",
                    lazy: () =>
                      import("../Tours/Riders/Rider06Hospitality.page"),
                  },
                  {
                    path: "merch",
                    lazy: () => import("../Tours/Riders/Rider07Merch.page"),
                  },
                  {
                    path: "supporting-act",
                    lazy: () => import("../Tours/Riders/Rider08Support.page"),
                  },
                ],
              },
              {
                path: "advance-tools/riders/:riderId/edit",
                lazy: () => import("../Tours/Riders/RiderForm.page"),
              },
              {
                path: "advance-tools/riders/:riderId/personnel/add",
                lazy: () => import("../Tours/Riders/RiderContactsAdd.page"),
              },
              {
                path: "advance-tools/riders/:riderId/support-personnel/add",
                lazy: () =>
                  import("../Tours/Riders/RiderSupportContactsAdd.page"),
              },
              {
                path: "advance-tools/riders/:riderId/labor/add",
                lazy: () => import("../Tours/Riders/RiderLaborAdd.page"),
              },
              {
                path: "advance-tools/riders/:riderId/preview",
                lazy: () => import("../Tours/Riders/Preview/RiderPreview.page"),
              },
              {
                path: "advance-tools/advance/:advanceId/review",
                lazy: () => import("../Artist/Advance/AdvanceReview.page"),
              },
              {
                path: "advance-tools/advance/:advanceId/review/:venueSubmissionId",
                lazy: () => import("../Artist/Advance/AdvanceVenueForm.page"),
                loader: async ({ params }) => {
                  if (!params.venueSubmissionId) {
                    return {};
                  }
                  await queryClient.prefetchQuery(
                    advanceVenueSubmissionQueryOptions(params.venueSubmissionId)
                  );
                  return {};
                },
              },
              {
                path: "advance-tools/advance/venues",
                lazy: () => import("../Artist/Advance/AdvanceVenuesList.page"),
              },
              {
                path: "advance-tools/advance/questions",
                lazy: () => import("../Artist/Advance/AdvanceQuestions.page"),
              },
              {
                path: "advance-tools/advance/create-email",
                lazy: () => import("../Artist/Advance/AdvanceCreateEmail.page"),
              },
              {
                path: "messages",
                loader: async () => {
                  await queryClient.prefetchQuery(chatRoomsQueryOptions);

                  return {};
                },
                lazy: () => import("../Messages/Messages.page"),
              },
              {
                path: "messages/:chatId/edit",
                lazy: () => import("../Messages/CreateChatRoomForm.page"),
              },
              {
                path: "messages/:chatId/members",
                lazy: () => import("../Messages/ChatRoomMembers.page"),
              },
              {
                path: "messages/add",
                lazy: () => import("../Messages/CreateChatRoomForm.page"),
              },
              {
                path: "reports",
                lazy: () => import("../Tours/Reports/Reports.page"),
                loader: async ({ params }) => {
                  if (!params.tourId) {
                    return {};
                  }

                  const tour = await queryClient.ensureQueryData(
                    tourQueryOptions(params.tourId)
                  );

                  const notAllowedRoles = [TourRoles.TOUR_LEAD];
                  if (!tour.role || notAllowedRoles.includes(tour.role)) {
                    return redirect("..");
                  }

                  return {};
                },
              },
              {
                path: "reports/:reportId",
                lazy: () => import("../Tours/Reports/ReportDetails.page"),
              },
              {
                path: "reports/new/:reportId",
                loader: async () => {
                  await queryClient.prefetchQuery(
                    reportQuestionsColumnsQueryOptions
                  );
                  return {};
                },
                lazy: () =>
                  import("../Tours/Reports/ReportsCustomCreation.page"),
              },
              {
                path: "travel-tracker",
                lazy: () => import("../Tours/Travel/TravelTracker.page"),
              },
              {
                path: "production-tracker",
                lazy: () =>
                  import("../Tours/ProductionTracker/ProductionTracker.page"),
              },
              {
                path: "files",
                lazy: () => import("../Tours/Files/TourFiles.page"),
              },
              {
                path: "supplemental-questions",
                lazy: () =>
                  import(
                    "../Tours/SupplementalQuestions/TourSupplementalQuestions.page"
                  ),
              },
              {
                path: ACCEPT_TOUR_INVITATION,
                loader: async ({ params }) => {
                  if (!params.tourId) return {};

                  await queryClient.prefetchQuery(
                    teamInvitationsQueryOptions()
                  );
                  return {};
                },
                lazy: () => import("../Tours/TourAcceptEmailInvitation.tsx"),
              },
            ],
          },
          {
            path: "admin",
            loader: async () => {
              //TODO: Add admin auth check once implemented on BE
              return {};
            },
            children: [
              {
                path: "users",
                lazy: () => import("../Admin/Users/AdminUsers.page"),
              },
              {
                path: "users/:userId",
                lazy: () => import("../Admin/Users/AdminUserDetails.page"),
              },
              {
                path: "users/create",
                lazy: () => import("../Admin/Users/AdminUserCreation.page"),
              },
              {
                path: "countries",
                lazy: () => import("../Admin/Countries/AdminCountries.page"),
              },
              {
                path: "countries/:countryId",
                lazy: () =>
                  import("../Admin/Countries/AdminCountryDetails.page"),
              },
              {
                path: "countries/create",
                lazy: () =>
                  import("../Admin/Countries/AdminCountryCreation.page"),
              },
            ],
          },
          {
            path: "design-system",
            lazy: () => import("../DesignSystem/DesignSystem.page"),
          },
        ],
      },
    ],
  },
] satisfies RouteObject[];

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

type RouterOpts = Parameters<typeof createBrowserRouter>[1];

export const router: ReturnType<typeof createBrowserRouter> =
  sentryCreateBrowserRouter(routes, {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
    },
  } satisfies RouterOpts);
